/**
 * Client full names live in one `name` field. These helpers infer surname vs given-name
 * so we can show "Family, Given Middle …" without splitting on the last token only,
 * which breaks compound names (e.g. "Dela Cruz") and mishandles suffixes (Jr., III).
 */

const SUFFIX_PATTERN = /^(jr\.?|sr\.?|ii|iii|iv|v|vi|vii|viii|ix|esq\.?)$/i;

/** Token is often the first particle of a multi-word Hispanic / Filipino surname. */
const SURNAME_JOINING_PREFIXES = new Set([
  'de',
  'del',
  'dela',
  'delos',
  'delas',
  'delle',
  'della',
  'van',
  'von',
  'san',
  'santa',
  'sto',
  'sta',
  'santo',
  'dos',
  'das',
  'den',
  'der',
  'ten',
  'ter',
]);

function stripDiacritics(s) {
  return s.normalize('NFD').replace(/\p{M}/gu, '');
}

/** Lowercase ASCII-ish key for prefixes / lookups */
function lcKey(tok) {
  return stripDiacritics(tok).replace(/[^\p{L}\p{N}']/gu, '').toLowerCase();
}

/**
 * @returns {{ surname: string, given: string, suffix: string }}
 */
export function splitFullNameIntoParts(fullName) {
  if (!fullName || typeof fullName !== 'string') {
    return { surname: '', given: '', suffix: '' };
  }

  let tokens = fullName.trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) {
    return { surname: '', given: '', suffix: '' };
  }

  let suffix = '';
  while (tokens.length > 1 && SUFFIX_PATTERN.test(tokens[tokens.length - 1])) {
    suffix = tokens.pop() + (suffix ? ` ${suffix}` : '');
  }

  if (tokens.length === 0) {
    return { surname: '', given: '', suffix };
  }

  if (tokens.length === 1) {
    return { surname: '', given: tokens[0], suffix };
  }

  /** @type string[] — family name fragments from the end */
  const surnameTokens = [];

  let i = tokens.length - 1;
  surnameTokens.unshift(tokens[i]);

  // Always take the substantive last surname word first; then peel joiners and "De …" spans.
  for (i = i - 1; i >= 0; ) {
    const t = tokens[i];
    const k = lcKey(t);
    const prev = i > 0 ? lcKey(tokens[i - 1]) : '';

    if (SURNAME_JOINING_PREFIXES.has(k)) {
      surnameTokens.unshift(tokens[i]);
      i -= 1;
      continue;
    }

    // "De La / Los / Las …" surname particles
    if ((k === 'la' || k === 'las' || k === 'los' || k === 'lo') && prev === 'de') {
      surnameTokens.unshift(tokens[i]);
      i -= 1;
      continue;
    }

    break;
  }

  const givenTokens = i >= 0 ? tokens.slice(0, i + 1) : [];
  return {
    surname: surnameTokens.join(' '),
    given: givenTokens.join(' ').trim(),
    suffix,
  };
}

/**
 * "Dela Cruz, Kayden" / "Smith, Ana Marie Jr." / single-token names stay unchanged (plus suffix).
 */
export function formatNameLastCommaFirst(fullName) {
  const { surname, given, suffix } = splitFullNameIntoParts(fullName);

  if (!surname && !given) {
    return (fullName || '').trim();
  }

  // One given name + optional suffix ("John Jr."), or mononym
  if (!surname && given) {
    return suffix ? `${given} ${suffix}`.trim() : given;
  }

  if (!given) {
    const rest = suffix ? `${surname}, ${suffix}` : surname;
    return rest.trim();
  }

  let out = `${surname}, ${given}`;
  if (suffix) {
    out += ` ${suffix}`;
  }
  return out.trim();
}

/**
 * Sort keyed by inferred family name, then given (stable enough for roster lists).
 * `a`, `b` are objects with a string `.name`.
 */
export function compareNamesByFamilyName(a, b) {
  const pa = splitFullNameIntoParts(a?.name ?? '');
  const pb = splitFullNameIntoParts(b?.name ?? '');
  const surn = lcKey(pa.surname).localeCompare(lcKey(pb.surname));
  if (surn !== 0) return surn;
  const giv = lcKey(pa.given).localeCompare(lcKey(pb.given));
  if (giv !== 0) return giv;
  return lcKey(pa.suffix).localeCompare(lcKey(pb.suffix));
}
